/* eslint-disable react/prop-types */
import * as Yup from 'yup';
import { Button, Spinner } from 'react-bootstrap';
import { Form as FormikForm, Formik } from 'formik';
import { createWorklogDay, updateWorklogDay } from '../../utils/API/worklog';
import { ExclamationTriangle } from 'react-bootstrap-icons';
import FormCheckbox from '../../components/Form/FormCheckbox';
import FormItem from '../../components/Form/FormItem';
import moment from 'moment';
import FormDatePicker from '../../components/Form/FormDatePicker';
import ActivitySelect from './ActivitySelect';

const initialValues = {
  activity: '',
  description: '',
  dataDa: moment(),
  dataA: moment(),
  hours: 8,
  nightTimeSpent: 0,
  id: undefined,
  isPeriod: false,
  createAnother: false,
};

async function onSubmit(values, actions, close, onDataChanged) {
  const { setSubmitting, resetForm } = actions;
  const {
    activity,
    dataA,
    dataDa,
    description,
    hours,
    nightTimeSpent,
    id,
    isPeriod,
    createAnother,
  } = values;
  const data = {
    time_spent: hours,
    night_time_spent: nightTimeSpent,
    description,
    issue: parseInt(activity, 10),
    date: isPeriod ? dataA.format('YYYY-MM-DD') : dataDa.format('YYYY-MM-DD'),
    from_date: isPeriod ? dataDa.format('YYYY-MM-DD') : '',
    id,
  };

  try {
    if (id) {
      await updateWorklogDay(data);
    } else {
      await createWorklogDay(data);
    }

    onDataChanged();

    if (!id && createAnother) {
      // "Aggiungi un altro": il salvataggio è già avvenuto sul BE,
      // riapriamo una maschera pulita e indipendente mantenendo la data.
      resetForm({
        values: {
          ...initialValues,
          createAnother: true,
          isPeriod,
          dataDa,
          dataA,
        },
      });
    } else {
      close();
    }
  } finally {
    setSubmitting(false);
  }
}

const validationSchema = Yup.object().shape({
  isPeriod: Yup.boolean(),
  createAnother: Yup.boolean(),
  activity: Yup.string().required('Campo obbligatorio'),
  description: Yup.string()
    .min(5, 'Lunghezza minima 5 caratteri')
    .max(50, 'Lunghezza massima 50 caratteri'),
  dataDa: Yup.date()
    .required('Campo obbligatorio')
    .test(
      'start_time_test',
      'Data non può essere maggiore di Data Fine',
      function check(value) {
        const { dataA, isPeriod } = this.parent;
        if (isPeriod) {
          if (dataA instanceof Date && !Number.isNaN(dataA.valueOf())) {
            if (Date.parse(value) > Date.parse(dataA)) {
              return false;
            }
          }
        }
        return true;
      }
    ),
  dataA: Yup.date().when('isPeriod', {
    is: true,
    then: Yup.date()
      .required('Campo obbligatorio')
      .min(Yup.ref('dataDa'), 'Data Fine non può essere minore di Data'),
  }),
  hours: Yup.number()
    .required('Campo obbligatorio')
    .positive("Valore non ammesso, dev'essere maggiore di 0"),
  nightTimeSpent: Yup.number()
    .min(0, 'Non può essere negativo')
    .max(8, 'Le ore notturne non possono superare 8')
    .test(
      'night_time_spent_max',
      'Le ore notturne sono più del totale',
      function (value) {
        const { hours } = this.parent;
        if (value === undefined || value === null) return true;
        return value <= hours;
      }
    ),
});

const WorklogForm = (props) => {
  const {
    month,
    userProjects,
    userIssues,
    setUserIssues,
    close,
    editFormData,
    onDataChanged,
  } = props;

  const isFutureMonth = month
    .startOf('month')
    .isAfter(moment().startOf('month'));
  const warn = isFutureMonth
    ? 'Per i mesi successivi, si possono inserire ore solo su assenze programmate, non su attività lavorative'
    : null;

  if (editFormData) {
    initialValues.dataDa = editFormData.date
      ? moment(editFormData.date, 'YYYY-MM-DD')
      : moment();
    initialValues.description = editFormData.description;
    initialValues.activity = editFormData.issue;
    initialValues.hours = editFormData.time_spent;
    initialValues.nightTimeSpent = editFormData.night_time_spent || 0;
    initialValues.id = editFormData.id;
    initialValues.isPeriod = false;
    initialValues.dataA = moment();
  }

  const hasId = !!initialValues?.id;

  return (
    <Formik
      validationSchema={validationSchema}
      onSubmit={(values, actions) =>
        onSubmit(values, actions, close, onDataChanged)
      }
      initialValues={initialValues}
    >
      {({ values, handleSubmit, isSubmitting, dirty, isValid, isValidating }) => {
        const buttonText = hasId ? 'Aggiorna' : 'Inserisci';

        return (
          <FormikForm noValidate onSubmit={handleSubmit}>
            <FormItem
              values={values}
              type="text"
              name="id"
              style={{ display: 'none' }}
              value={undefined}
            />
            {warn && (
              <div
                style={{ color: 'red', display: 'flex', alignItems: 'center' }}
              >
                <span style={{ marginRight: '10px' }}>
                  <ExclamationTriangle />
                </span>
                <span>{warn}</span>
              </div>
            )}

            <ActivitySelect
              name="activity"
              label="Attività *"
              userProjects={userProjects}
              userIssues={userIssues}
              setUserIssues={setUserIssues}
              isFutureMonth={isFutureMonth}
              disabled={hasId}
            />
            <FormItem
              type="textarea"
              name="description"
              rows="1"
              label="Descrizione"
            />
            <FormDatePicker
              name="dataDa"
              label="Data *"
              minDate={month.clone().startOf('month')}
              maxDate={month.clone().endOf('month')}
              disabled={hasId}
            />
            {!hasId && (
              <>
                <FormCheckbox name="isPeriod" label="Periodo" />
                <FormDatePicker
                  name="dataA"
                  label="Data Fine *"
                  minDate={month.clone().startOf('month')}
                  maxDate={month.clone().endOf('month')}
                  disabled={!values.isPeriod}
                />
              </>
            )}
            <div
              style={{
                display: 'flex',
                gap: '1rem',
                alignItems: 'flex-start',
                minHeightheight: '104px',
                width: '100%',
                paddingBottom: '1rem',
              }}
            >
              <FormItem
                type="number"
                name="hours"
                label="Totale ore lavorate *"
                style={{ flex: 1, minWidth: 0 }}
              />
              {/* TODO: togliere commento al rilascio delle straordinarie
              <FormItem
                type="number"
                name="nightTimeSpent"
                label="di cui notturne (22:00-6:00)"
                min={0}
                max={values.hours}
                style={{ flex: 1, minWidth: 0 }}
              />
              */}
            </div>
            <div className="buttonContent">
              {!hasId && (
                <FormCheckbox name="createAnother" label="Aggiungi un altro" />
              )}
              <div className="spacer" />
              <Button variant="secondary" onClick={close}>
                Annulla
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={isSubmitting || !dirty || isValidating || !isValid}
              >
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      style={{ marginRight: '8px' }}
                    />
                    {buttonText}
                  </>
                ) : (
                  buttonText
                )}
              </Button>
            </div>
          </FormikForm>
        );
      }}
    </Formik>
  );
};

export default WorklogForm;
